import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminApi, editionApi } from '../services/api';
import TopBar from '../components/layout/TopBar';
import BackButton from '../components/BackButton';
import { extractAxiosError } from '../components/SafeError';
import { 
  Search, 
  User, 
  Shield, 
  Loader2, 
  LogOut, 
  Mail, 
  Ban, 
  Trash2, 
  Gift,
  ArrowLeft,
  Clock,
  Monitor,
  CheckCircle,
  XCircle,
  Globe,
  Package,
  DollarSign,
  TrendingUp,
  Users,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Plane,
  MapPin,
  Database,
  Play,
  AlertTriangle,
  CheckSquare,
  Square,
  UserX
} from 'lucide-react';
// Display-only label for the legacy "MSFS A320 Edition" DB record. The stored
// edition `name` and all entitlement/grant keys stay exactly as-is — legacy
// access depends on the exact string. This only changes what admins SEE.
const EDITION_DISPLAY_NAMES = {
  'MSFS A320 Edition': 'Fenix A320 Edition',
};
const editionDisplayName = (name) => EDITION_DISPLAY_NAMES[name] || name;
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import api from '../services/api';

const AdminPage = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editions, setEditions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Filter state
  const [activeFilter, setActiveFilter] = useState('');
  
  // Revenue Dashboard state
  const [revenueData, setRevenueData] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(true);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const USERS_PER_PAGE = 20;
  
  // Debounce timer ref
  const debounceTimerRef = useRef(null);
  
  // Dialogs
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [showGrantDialog, setShowGrantDialog] = useState(false);
  const [selectedEdition, setSelectedEdition] = useState('');
  
  // Database Migrations state
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [migrationResults, setMigrationResults] = useState(null);
  const [showMigrationConfirm, setShowMigrationConfirm] = useState(false);
  const [migrationInfo, setMigrationInfo] = useState(null);

  // Bulk delete state
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleteConfirmText, setBulkDeleteConfirmText] = useState('');
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [bulkDeleteResults, setBulkDeleteResults] = useState(null);

  useEffect(() => {
    searchUsers('');
    fetchEditions();
    fetchRevenueData();
    fetchMigrationInfo();
  }, []);

  const fetchMigrationInfo = async () => {
    try {
      const { data } = await api.get('/admin/migration-info');
      setMigrationInfo(data);
    } catch (err) {
      console.error('Failed to fetch migration info:', err);
    }
  };

  const fetchRevenueData = async () => {
    try {
      setRevenueLoading(true);
      const { data } = await api.get('/admin/revenue');
      setRevenueData(data);
    } catch (err) {
      console.error('Failed to fetch revenue data:', err);
    } finally {
      setRevenueLoading(false);
    }
  };

  const fetchEditions = async () => {
    try {
      const { data } = await editionApi.getAll();
      setEditions(data);
    } catch (err) {
      console.error('Failed to fetch editions:', err);
    }
  };

  const searchUsers = async (query, page = 1, filter = activeFilter) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(`/admin/users/search?q=${encodeURIComponent(query)}&page=${page}&limit=${USERS_PER_PAGE}&filter=${filter}`);
      setUsers(data.users);
      setTotalPages(data.pages);
      setTotalUsers(data.total);
      setCurrentPage(data.page);
    } catch (err) {
      setError(extractAxiosError(err) || 'Failed to search users');
    } finally {
      setLoading(false);
    }
  };

  // Handle filter change
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setCurrentPage(1);
    setSelectedUserIds(new Set());
    searchUsers(searchQuery, 1, filter);
  };

  // Debounced search - 300ms delay
  const debouncedSearch = useCallback((query) => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Reset to page 1 on new search
    setCurrentPage(1);
    
    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      searchUsers(query);
    }, 300);
  }, []);

  // Handle search input change with debounce
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    searchUsers(searchQuery);
  };

  const selectUser = async (userId) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const { data } = await adminApi.getUserDetail(userId);
      setSelectedUser(data);
    } catch (err) {
      setError(extractAxiosError(err) || 'Failed to fetch user details');
    } finally {
      setLoading(false);
    }
  };

  const handleForceLogout = async () => {
    if (!selectedUser) return;
    setActionLoading('logout');
    setError('');
    try {
      const { data } = await adminApi.forceLogout(selectedUser.id);
      setSuccess(data.message);
      await selectUser(selectedUser.id);
    } catch (err) {
      setError(extractAxiosError(err) || 'Failed to force logout');
    } finally {
      setActionLoading('');
    }
  };

  const handleSendReset = async () => {
    if (!selectedUser) return;
    setActionLoading('reset');
    setError('');
    try {
      const { data } = await adminApi.sendPasswordReset(selectedUser.id);
      setSuccess(data.message);
    } catch (err) {
      setError(extractAxiosError(err) || 'Failed to send reset');
    } finally {
      setActionLoading('');
    }
  };

  const handleResendVerification = async () => {
    if (!selectedUser) return;
    setActionLoading('resend-verify');
    setError('');
    try {
      const { data } = await adminApi.resendVerification(selectedUser.id);
      setSuccess(data.message);
    } catch (err) {
      setError(extractAxiosError(err) || 'Failed to resend verification');
    } finally {
      setActionLoading('');
    }
  };

  const handleSuspend = async () => {
    if (!selectedUser) return;
    setActionLoading('suspend');
    setError('');
    try {
      if (selectedUser.is_suspended) {
        const { data } = await adminApi.unsuspendUser(selectedUser.id);
        setSuccess(data.message);
      } else {
        const { data } = await adminApi.suspendUser(selectedUser.id);
        setSuccess(data.message);
      }
      await selectUser(selectedUser.id);
      setShowSuspendDialog(false);
    } catch (err) {
      setError(extractAxiosError(err) || 'Failed to change suspension status');
    } finally {
      setActionLoading('');
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    setActionLoading('delete');
    setError('');
    try {
      const { data } = await adminApi.deleteUser(selectedUser.id);
      setSuccess(data.message);
      setSelectedUser(null);
      searchUsers(searchQuery);
      setShowDeleteDialog(false);
    } catch (err) {
      setError(extractAxiosError(err) || 'Failed to delete user');
    } finally {
      setActionLoading('');
    }
  };

  const handleGrantEdition = async () => {
    if (!selectedUser || !selectedEdition) return;
    setActionLoading('grant');
    setError('');
    try {
      const { data } = await adminApi.grantEdition(selectedUser.id, selectedEdition);
      setSuccess(data.message);
      await selectUser(selectedUser.id);
      setShowGrantDialog(false);
      setSelectedEdition('');
    } catch (err) {
      setError(extractAxiosError(err) || 'Failed to grant edition');
    } finally {
      setActionLoading('');
    }
  };

  const handleToggleEdition = async (editionId, currentlyActive) => {
    if (!selectedUser) return;
    setActionLoading(`toggle-${editionId}`);
    setError('');
    try {
      if (currentlyActive) {
        // Remove edition
        const { data } = await adminApi.removeEdition(selectedUser.id, editionId);
        setSuccess(data.message);
      } else {
        // Grant edition
        const { data } = await adminApi.grantEdition(selectedUser.id, editionId);
        setSuccess(data.message);
      }
      await selectUser(selectedUser.id);
    } catch (err) {
      setError(extractAxiosError(err) || 'Failed to toggle edition');
    } finally {
      setActionLoading('');
    }
  };

  // Run Database Migrations
  const handleRunMigrations = async () => {
    setMigrationLoading(true);
    setMigrationResults(null);
    setError('');
    setSuccess('');
    try {
      const { data } = await api.post('/admin/run-migrations');
      setMigrationResults(data);
      if (data.success) {
        setSuccess(`Database migrations completed: ${data.summary}`);
      } else {
        setError(`Some migrations failed: ${data.summary}`);
      }
    } catch (err) {
      setError(extractAxiosError(err) || 'Failed to run migrations');
    } finally {
      setMigrationLoading(false);
      setShowMigrationConfirm(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  // Bulk delete handlers
  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const selectAllUnpaid = () => {
    const unpaidIds = users.filter(u => !u.has_paid && u.role !== 'founder').map(u => u.id);
    setSelectedUserIds(new Set(unpaidIds));
  };

  const deselectAll = () => {
    setSelectedUserIds(new Set());
  };

  const handleBulkDelete = async () => {
    if (bulkDeleteConfirmText !== 'DELETE UNPAID USERS') return;
    setBulkDeleteLoading(true);
    setError('');
    setSuccess('');
    try {
      const { data } = await adminApi.bulkDeleteUsers(
        Array.from(selectedUserIds),
        bulkDeleteConfirmText
      );
      setBulkDeleteResults(data);
      setSuccess(`Bulk delete complete: ${data.deleted_count} user(s) removed.`);
      setSelectedUserIds(new Set());
      setBulkDeleteMode(false);
      setShowBulkDeleteDialog(false);
      setBulkDeleteConfirmText('');
      searchUsers(searchQuery);
    } catch (err) {
      const errData = err?.response?.data?.detail;
      if (errData?.blocked_users) {
        setError(`BLOCKED: ${errData.message}`);
      } else {
        setError(extractAxiosError(err) || 'Bulk delete failed');
      }
      setShowBulkDeleteDialog(false);
      setBulkDeleteConfirmText('');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  // Protect all founder accounts from destructive actions
  const isProtectedAccount = (user) => {
    return user?.is_primary_founder || user?.role === 'founder';
  };
  const isFounderAccount = selectedUser?.is_primary_founder || selectedUser?.role === 'founder';
  // Backwards compatibility alias
  const isPrimaryFounder = isFounderAccount;

  return (
    <div className="h-screen flex flex-col bg-cockpit-bg" data-testid="admin-page">
      <TopBar />
      
      <div className="flex-1 flex overflow-hidden">
        {/* User List */}
        <aside className="w-80 bg-cockpit-panel border-r border-cockpit-border flex flex-col">
          <div className="p-4 border-b border-cockpit-border">
            {/* Back to Dashboard button */}
            <div className="mb-4">
              <BackButton to="/dashboard" label="Back to Dashboard" />
            </div>
            
            <h2 className="text-lg font-medium text-cockpit-text-primary mb-4">Admin Dashboard</h2>
            
            <form onSubmit={handleSearch}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cockpit-text-secondary" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search by email..."
                  className="input-field pl-10"
                  data-testid="admin-search"
                />
                {loading && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-airbus-blue animate-spin" />
                )}
              </div>
            </form>
            
            {/* Filter Pills */}
            <div className="flex flex-wrap gap-1.5 mt-3">
              {[
                { key: '', label: 'All' },
                { key: 'active', label: 'Active Now' },
                { key: 'new', label: 'New Users' },
                { key: 'paid', label: 'Paid' },
                { key: 'unpaid', label: 'Unpaid' },
                { key: 'granted', label: 'Granted' },
                { key: 'suspended', label: 'Suspended' },
                { key: 'email_opened', label: 'Opened' },
                { key: 'email_sent', label: 'Sent' },
                { key: 'email_not_sent', label: 'Not Sent' },
              ].map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => handleFilterChange(filter.key)}
                  className={`px-2.5 py-1 text-[10px] font-medium rounded-sm transition-colors ${
                    activeFilter === filter.key
                      ? 'bg-fsa-blue text-white'
                      : 'bg-cockpit-bg text-cockpit-text-secondary hover:bg-cockpit-border'
                  }`}
                  data-testid={`filter-${filter.key || 'all'}`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          {/* Bulk Mode Controls */}
          <div className="px-4 py-2 border-b border-cockpit-border bg-cockpit-panel">
            <div className="flex items-center justify-between">
              <button
                onClick={() => {
                  setBulkDeleteMode(!bulkDeleteMode);
                  if (bulkDeleteMode) setSelectedUserIds(new Set());
                }}
                className={`px-2.5 py-1 text-[10px] font-medium rounded-sm transition-colors ${
                  bulkDeleteMode
                    ? 'bg-red-900/40 text-red-300 border border-red-700/50'
                    : 'bg-cockpit-bg text-cockpit-text-secondary hover:bg-cockpit-border'
                }`}
                data-testid="toggle-bulk-mode"
              >
                <UserX className="w-3 h-3 inline mr-1" />
                {bulkDeleteMode ? 'Exit Bulk Mode' : 'Manage Users'}
              </button>
              {bulkDeleteMode && (
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={selectAllUnpaid}
                    className="px-2 py-1 text-[10px] font-medium rounded-sm bg-cockpit-bg text-cockpit-text-secondary hover:bg-cockpit-border"
                    data-testid="select-all-unpaid"
                  >
                    Select Page
                  </button>
                  <button
                    onClick={deselectAll}
                    className="px-2 py-1 text-[10px] font-medium rounded-sm bg-cockpit-bg text-cockpit-text-secondary hover:bg-cockpit-border"
                    data-testid="deselect-all"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
            
            {/* Manual selection actions */}
            {bulkDeleteMode && selectedUserIds.size > 0 && (
              <div className="mt-1.5 flex flex-col gap-1.5">
                <p className="text-[10px] text-cockpit-text-secondary italic">
                  {selectedUserIds.size} selected
                </p>
                <button
                  onClick={() => setShowBulkDeleteDialog(true)}
                  className="w-full px-3 py-1.5 text-xs font-medium rounded-sm bg-red-900/50 text-red-300 border border-red-700/50 hover:bg-red-900/70 transition-colors"
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-3 h-3 inline mr-1.5" />
                  Delete {selectedUserIds.size} Selected
                </button>
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading && !selectedUser && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-airbus-blue animate-spin" />
              </div>
            )}

            {users.map(u => (
              <div
                key={u.id}
                className={`w-full p-3 text-left border-b border-cockpit-border hover:bg-cockpit-bg transition-colors flex items-center gap-2 ${
                  selectedUser?.id === u.id ? 'bg-cockpit-bg' : ''
                } ${bulkDeleteMode && selectedUserIds.has(u.id) ? 'bg-red-900/10' : ''}`}
                data-testid={`user-item-${u.id}`}
              >
                {/* Checkbox in bulk mode */}
                {bulkDeleteMode && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (u.has_paid || u.role === 'founder') return;
                      toggleUserSelection(u.id);
                    }}
                    className={`flex-shrink-0 ${(u.has_paid || u.role === 'founder') ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
                    data-testid={`checkbox-${u.id}`}
                    disabled={u.has_paid || u.role === 'founder'}
                  >
                    {selectedUserIds.has(u.id) ? (
                      <CheckSquare className="w-4 h-4 text-red-400" />
                    ) : (
                      <Square className="w-4 h-4 text-cockpit-text-secondary" />
                    )}
                  </button>
                )}
                
                {/* User card (clickable to view details) */}
                <button
                  onClick={() => selectUser(u.id)}
                  className="flex-1 text-left min-w-0"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 bg-cockpit-bg rounded-sm flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-cockpit-text-secondary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-cockpit-text-primary truncate">{u.email}</p>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <span className={`text-[10px] ${
                          u.role === 'founder' ? 'text-status-warning' : 'text-cockpit-text-secondary'
                        }`}>
                          {u.role}
                        </span>
                        {/* Paid/Unpaid indicator */}
                        {u.role !== 'founder' && (
                          <span className={`text-[10px] px-1.5 py-0 rounded-sm font-medium ${
                            u.has_paid
                              ? 'bg-status-success/20 text-status-success'
                              : 'bg-red-900/30 text-red-400'
                          }`}>
                            {u.has_paid ? 'PAID' : 'UNPAID'}
                          </span>
                        )}
                        {/* Days since signup */}
                        {u.days_since_signup !== undefined && (
                          <span className="text-[10px] text-cockpit-text-secondary/60">
                            {u.days_since_signup}d ago
                          </span>
                        )}
                        {/* Email status indicator */}
                        {u.role !== 'founder' && u.email_status === 'opened' && (
                          <span className="text-[10px] px-1.5 py-0 rounded-sm font-medium bg-status-success/20 text-status-success" title={u.email_opened_at ? `Opened: ${new Date(u.email_opened_at).toLocaleString()}` : ''}>
                            OPENED
                          </span>
                        )}
                        {u.role !== 'founder' && (u.email_status === 'sent' || u.email_status === 'delivered') && (
                          <span className="text-[10px] px-1.5 py-0 rounded-sm font-medium bg-fsa-blue/20 text-fsa-blue" title={u.email_sent_at ? `Sent: ${new Date(u.email_sent_at).toLocaleString()}` : ''}>
                            SENT
                          </span>
                        )}
                        {u.role !== 'founder' && u.email_status === 'not_sent' && (
                          <span className="text-[10px] px-1.5 py-0 rounded-sm font-medium bg-cockpit-border/50 text-cockpit-text-secondary/60">
                            NO EMAIL
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      {u.is_suspended && (
                        <Ban className="w-3.5 h-3.5 text-status-error" />
                      )}
                      {u.role === 'founder' && (
                        <Shield className="w-3.5 h-3.5 text-status-warning" />
                      )}
                    </div>
                  </div>
                </button>
              </div>
            ))}
          </div>

          {/* Pagination Controls - Fixed at bottom of sidebar */}
          {totalPages > 1 && (
            <div className="p-3 border-t border-cockpit-border bg-cockpit-panel shrink-0">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => searchUsers(searchQuery, currentPage - 1)}
                  disabled={currentPage <= 1 || loading}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-cockpit-text-secondary hover:text-cockpit-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="prev-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Prev
                </button>
                <span className="text-xs text-cockpit-text-secondary">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => searchUsers(searchQuery, currentPage + 1)}
                  disabled={currentPage >= totalPages || loading}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-cockpit-text-secondary hover:text-cockpit-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="next-page"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-cockpit-text-secondary/60 text-center mt-1">
                {totalUsers} total users
              </p>
            </div>
          )}
        </aside>

        {/* User Detail */}
        <main className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-6 p-3 bg-red-900/30 border border-red-900 rounded-sm">
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-3 bg-status-success/20 border border-status-success/50 rounded-sm">
              <p className="text-sm text-status-success">{success}</p>
            </div>
          )}

          {!selectedUser ? (
            <div className="space-y-6">
              {/* Revenue Dashboard */}
              <div className="card-panel p-6">
                <h3 className="text-lg font-medium text-cockpit-text-primary mb-4">Revenue Dashboard</h3>
                
                {revenueLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-airbus-blue animate-spin" />
                  </div>
                ) : revenueData ? (
                  <>
                    {/* Metrics Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="p-4 bg-cockpit-bg border border-cockpit-border rounded-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <DollarSign className="w-4 h-4 text-status-success" />
                          <span className="text-xs text-cockpit-text-secondary">Total Revenue</span>
                        </div>
                        <p className="text-2xl font-semibold text-cockpit-text-primary">
                          £{revenueData.total_revenue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                      
                      <div className="p-4 bg-cockpit-bg border border-cockpit-border rounded-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="w-4 h-4 text-fsa-blue" />
                          <span className="text-xs text-cockpit-text-secondary">Sales Today</span>
                        </div>
                        <p className="text-2xl font-semibold text-cockpit-text-primary">
                          £{revenueData.sales_today.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                      
                      <div className="p-4 bg-cockpit-bg border border-cockpit-border rounded-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <CreditCard className="w-4 h-4 text-status-warning" />
                          <span className="text-xs text-cockpit-text-secondary">Sales This Month</span>
                        </div>
                        <p className="text-2xl font-semibold text-cockpit-text-primary">
                          £{revenueData.sales_month.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                      
                      <div className="p-4 bg-cockpit-bg border border-cockpit-border rounded-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <Users className="w-4 h-4 text-airbus-blue" />
                          <span className="text-xs text-cockpit-text-secondary">Total Customers</span>
                        </div>
                        <p className="text-2xl font-semibold text-cockpit-text-primary">
                          {revenueData.total_customers}
                        </p>
                      </div>
                    </div>

                    {/* Recent Transactions Table */}
                    <div>
                      <h4 className="text-sm font-medium text-cockpit-text-secondary mb-3">Recent Transactions</h4>
                      {revenueData.recent_transactions.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-cockpit-border">
                                <th className="text-left py-2 px-3 text-cockpit-text-secondary font-medium">User</th>
                                <th className="text-right py-2 px-3 text-cockpit-text-secondary font-medium">Amount</th>
                                <th className="text-left py-2 px-3 text-cockpit-text-secondary font-medium">Product</th>
                                <th className="text-left py-2 px-3 text-cockpit-text-secondary font-medium">Status</th>
                                <th className="text-left py-2 px-3 text-cockpit-text-secondary font-medium">Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {revenueData.recent_transactions.map((txn, idx) => (
                                <tr key={idx} className="border-b border-cockpit-border/50 hover:bg-cockpit-border/20">
                                  <td className="py-2 px-3 text-cockpit-text-primary">{txn.email}</td>
                                  <td className="py-2 px-3 text-cockpit-text-primary text-right font-mono">
                                    £{txn.amount.toFixed(2)}
                                  </td>
                                  <td className="py-2 px-3 text-cockpit-text-secondary">{txn.product}</td>
                                  <td className="py-2 px-3">
                                    <span className={`px-2 py-0.5 text-xs font-medium rounded-sm ${
                                      txn.status === 'paid' 
                                        ? 'bg-status-success/20 text-status-success' 
                                        : 'bg-cockpit-border text-cockpit-text-secondary'
                                    }`}>
                                      {txn.status}
                                    </span>
                                  </td>
                                  <td className="py-2 px-3 text-cockpit-text-secondary text-xs">
                                    {formatDate(txn.date)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-sm text-cockpit-text-secondary py-4 text-center">No transactions yet</p>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-cockpit-text-secondary text-center py-4">Failed to load revenue data</p>
                )}
              </div>

              {/* Database Migrations Panel */}
              <div className="card-panel p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Database className="w-5 h-5 text-fsa-blue" />
                  <h3 className="text-lg font-medium text-cockpit-text-primary">Database Migrations</h3>
                  <span className="px-2 py-0.5 text-xs font-medium rounded-sm bg-status-success/20 text-status-success">
                    Auto-Discovery
                  </span>
                </div>
                
                <p className="text-sm text-cockpit-text-secondary mb-4">
                  Automatically syncs all content changes to production after deployment. 
                  Scans for migration scripts and runs them safely.
                </p>

                {/* Available Scripts / Empty State */}
                {migrationInfo && (
                  migrationInfo.available_scripts?.length > 0 ? (
                    <div className="mb-4 p-4 bg-cockpit-bg border border-cockpit-border rounded-sm">
                      <p className="text-xs font-medium text-cockpit-text-secondary uppercase tracking-wider mb-3">
                        {migrationInfo.available_scripts.length} Scripts Detected
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {migrationInfo.available_scripts.map((script, idx) => (
                          <span key={idx} className="px-2 py-1 text-xs bg-fsa-blue/10 text-fsa-blue rounded-sm">
                            {script.display_name}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="mb-4 p-4 bg-cockpit-bg border border-cockpit-border rounded-sm" data-testid="no-migrations-message">
                      <p className="text-sm text-cockpit-text-secondary">
                        No migrations registered. Content updates are applied automatically at startup.
                      </p>
                    </div>
                  )
                )}

                {/* Protected Collections Notice */}
                <div className="mb-4 p-3 bg-amber-900/20 border border-amber-700/50 rounded-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-amber-400" />
                    <span className="text-xs font-medium text-amber-400 uppercase tracking-wider">Protected Data</span>
                  </div>
                  <p className="text-xs text-amber-300/80">
                    User accounts, sessions, payments, and purchases are NEVER modified by migrations.
                  </p>
                </div>

                {/* Migration Results */}
                {migrationResults && (
                  <div className={`mb-4 p-4 rounded-sm border ${
                    migrationResults.success 
                      ? 'bg-status-success/10 border-status-success/30' 
                      : 'bg-status-error/10 border-status-error/30'
                  }`}>
                    <p className={`text-sm font-medium mb-3 ${
                      migrationResults.success ? 'text-status-success' : 'text-status-error'
                    }`}>
                      {migrationResults.summary}
                    </p>
                    <div className="space-y-2">
                      {migrationResults.results.map((result, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          {result.success ? (
                            <CheckCircle className="w-4 h-4 text-status-success flex-shrink-0" />
                          ) : (
                            <XCircle className="w-4 h-4 text-status-error flex-shrink-0" />
                          )}
                          <span className="text-cockpit-text-primary font-medium">{result.script}:</span>
                          <span className="text-cockpit-text-secondary">{result.message}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {migrationInfo?.available_scripts?.length > 0 && (
                  <button
                    onClick={() => setShowMigrationConfirm(true)}
                    disabled={migrationLoading}
                    className="btn-primary flex items-center gap-2"
                    data-testid="run-migrations-btn"
                  >
                    {migrationLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Running Migrations...
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4" />
                        Run Database Migrations
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Select user prompt */}
              <div className="flex flex-col items-center justify-center py-8 text-cockpit-text-secondary">
                <Shield className="w-12 h-12 mb-4" />
                <p>Select a user from the list to view details</p>
              </div>

            </div>
          ) : (
            <div className="max-w-3xl">
              {/* Back button on mobile */}
              <button
                onClick={() => setSelectedUser(null)}
                className="md:hidden flex items-center gap-2 text-cockpit-text-secondary hover:text-cockpit-text-primary mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to list
              </button>

              {/* User header */}
              <div className="card-panel p-6 mb-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-airbus-blue/10 rounded-sm flex items-center justify-center">
                      <User className="w-8 h-8 text-airbus-blue" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-cockpit-text-primary">{selectedUser.email}</h2>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-sm ${
                          selectedUser.role === 'founder' 
                            ? 'bg-status-warning/20 text-status-warning' 
                            : 'bg-cockpit-border text-cockpit-text-secondary'
                        }`}>
                          {selectedUser.role?.toUpperCase()}
                        </span>
                        {isPrimaryFounder && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-sm bg-red-900/30 text-red-300">
                            PRIMARY FOUNDER – PROTECTED
                          </span>
                        )}
                        {selectedUser.is_suspended && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-sm bg-status-error/20 text-status-error">
                            SUSPENDED
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="label-text mb-1">Status</p>
                    <div className="flex items-center gap-2">
                      {selectedUser.is_suspended ? (
                        <span className="text-sm text-status-error font-medium">Suspended</span>
                      ) : (
                        <span className="text-sm text-status-success font-medium">Active</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="label-text mb-1">Verified</p>
                    <div className="flex items-center gap-2">
                      {selectedUser.is_verified ? (
                        <CheckCircle className="w-4 h-4 text-status-success" />
                      ) : (
                        <XCircle className="w-4 h-4 text-status-error" />
                      )}
                      <span className="text-sm text-cockpit-text-primary">
                        {selectedUser.is_verified ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="label-text mb-1">Created</p>
                    <p className="text-sm text-cockpit-text-primary">{formatDate(selectedUser.created_at)}</p>
                  </div>
                  <div>
                    <p className="label-text mb-1">Last Login</p>
                    <p className="text-sm text-cockpit-text-primary">{formatDate(selectedUser.last_login)}</p>
                  </div>
                  <div>
                    <p className="label-text mb-1">Downloads</p>
                    <p className="text-sm text-cockpit-text-primary">Downloads: {selectedUser.download_count ?? 0}</p>
                  </div>
                </div>
              </div>

              {/* Products Owned */}
              <div className="card-panel p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <Package className="w-4 h-4 text-fsa-blue" />
                  <h3 className="text-lg font-medium text-cockpit-text-primary">Products Owned</h3>
                </div>
                {selectedUser.has_full_access ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="px-2 py-1 text-xs font-medium bg-fsa-blue/20 text-fsa-blue rounded-sm">
                      Fenix A320 Edition (Lifetime)
                    </span>
                    {selectedUser.access_source && (
                      <span className="text-xs text-cockpit-text-secondary">
                        Source: {selectedUser.access_source}
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-cockpit-text-secondary">No products owned</p>
                )}
              </div>

              {/* Edition Access */}
              <div className="card-panel p-6 mb-6">
                <h3 className="text-lg font-medium text-cockpit-text-primary mb-4">Edition Access</h3>
                
                {/* Founder bypass notice */}
                {selectedUser.role === 'founder' && (
                  <div className="mb-4 p-3 bg-amber-900/20 border border-amber-700/50 rounded-sm">
                    <p className="text-sm text-amber-300">
                      <Shield className="w-4 h-4 inline mr-2" />
                      Founder accounts automatically have full access to all editions.
                    </p>
                  </div>
                )}

                {/* Edition toggles */}
                <div className="space-y-3">
                  {editions.map(edition => {
                    const userHasEdition = selectedUser.editions?.find(e => e.id === edition.id);
                    // Check BOTH user_edition_active AND products array for actual access
                    const hasProductAccess = selectedUser.products?.includes('core');
                    const isActive = hasProductAccess || userHasEdition?.user_edition_active;
                    const isToggling = actionLoading === `toggle-${edition.id}`;
                    const accessSource = userHasEdition?.access_source;
                    
                    return (
                      <div 
                        key={edition.id}
                        className="flex items-center justify-between p-4 bg-cockpit-bg border border-cockpit-border rounded-sm"
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium text-cockpit-text-primary">{editionDisplayName(edition.name)}</p>
                          <p className="text-xs text-cockpit-text-secondary">{edition.description}</p>
                          {isActive && (
                            <div className="mt-2 space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-cockpit-text-secondary">Access:</span>
                                <span className="text-xs font-medium text-status-success">Active</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-cockpit-text-secondary">Source:</span>
                                {accessSource === 'paid' ? (
                                  <span className="text-xs font-medium text-fsa-blue">Paid</span>
                                ) : accessSource === 'granted' ? (
                                  <span className="text-xs font-medium text-status-warning">Granted by Founder</span>
                                ) : hasProductAccess ? (
                                  <span className="text-xs font-medium text-status-warning">Product Access</span>
                                ) : (
                                  <span className="text-xs font-medium text-cockpit-text-secondary">Unknown</span>
                                )}
                              </div>
                              {userHasEdition && (
                                <p className="text-xs text-cockpit-text-secondary/60">
                                  Since: {formatDate(userHasEdition.purchase_date)}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          {isPrimaryFounder ? (
                            <span className="text-xs text-cockpit-text-secondary">Protected</span>
                          ) : (
                            <>
                              {isToggling && <Loader2 className="w-4 h-4 animate-spin text-airbus-blue" />}
                              <Switch
                                checked={isActive || false}
                                onCheckedChange={() => handleToggleEdition(edition.id, isActive)}
                                disabled={isToggling || isPrimaryFounder}
                                data-testid={`edition-toggle-${edition.id}`}
                              />
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {editions.length === 0 && (
                  <p className="text-sm text-cockpit-text-secondary">No editions available</p>
                )}
              </div>

              {/* Active Session */}
              <div className="card-panel p-6 mb-6">
                <h3 className="text-lg font-medium text-cockpit-text-primary mb-4">Active Session</h3>
                
                {selectedUser.active_session ? (
                  <div className="space-y-3">
                    {/* Current Phase - shows the live phase DISPLAY NAME (never a raw/old slug) */}
                    <div className="flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-sm">
                      <Plane className="w-5 h-5 text-emerald-400" />
                      <div>
                        <p className="text-sm text-emerald-400 font-medium">
                          {selectedUser.active_session.current_phase_title || 'Not started'}
                        </p>
                        <p className="text-xs text-cockpit-text-secondary">
                          Current Phase · {selectedUser.active_session.progress_ticked ?? 0} / {selectedUser.active_session.progress_total ?? 0} checks
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-cockpit-bg border border-cockpit-border rounded-sm">
                      <Monitor className="w-5 h-5 text-cockpit-text-secondary" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-cockpit-text-primary truncate">
                          {selectedUser.active_session.device_info}
                        </p>
                        <p className="text-xs text-cockpit-text-secondary">Device</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-cockpit-bg border border-cockpit-border rounded-sm">
                      <Globe className="w-5 h-5 text-cockpit-text-secondary" />
                      <div>
                        <p className="text-sm text-cockpit-text-primary font-mono">
                          {selectedUser.active_session.ip_address}
                        </p>
                        <p className="text-xs text-cockpit-text-secondary">IP Address</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-cockpit-bg border border-cockpit-border rounded-sm">
                      <MapPin className="w-5 h-5 text-cockpit-text-secondary" />
                      <div>
                        <p className="text-sm text-cockpit-text-primary">
                          {selectedUser.active_session.location_city && selectedUser.active_session.location_country
                            ? `${selectedUser.active_session.location_city}, ${selectedUser.active_session.location_country}`
                            : 'Unknown'}
                        </p>
                        <p className="text-xs text-cockpit-text-secondary">Location</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-cockpit-bg border border-cockpit-border rounded-sm">
                      <Clock className="w-5 h-5 text-cockpit-text-secondary" />
                      <div>
                        <p className="text-sm text-cockpit-text-primary">
                          {formatDate(selectedUser.active_session.created_at)}
                        </p>
                        <p className="text-xs text-cockpit-text-secondary">Session Started</p>
                      </div>
                    </div>
                    {/* Last Activity - Heartbeat */}
                    <div className="flex items-center gap-3 p-3 bg-fsa-blue/10 border border-fsa-blue/20 rounded-sm">
                      <Clock className="w-5 h-5 text-fsa-blue" />
                      <div>
                        <p className="text-sm text-fsa-blue font-medium">
                          {selectedUser.active_session.last_activity 
                            ? formatDate(selectedUser.active_session.last_activity)
                            : 'No recent activity'}
                        </p>
                        <p className="text-xs text-cockpit-text-secondary">Last Active</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-cockpit-text-secondary">No active session</p>
                )}
              </div>

              {/* Payment History - ADMIN VISIBILITY */}
              <div className="card-panel p-6 mb-6">
                <h3 className="text-lg font-medium text-cockpit-text-primary mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-fsa-blue" />
                  Payment History
                </h3>
                
                {selectedUser.payment_history && selectedUser.payment_history.length > 0 ? (
                  <div className="space-y-3">
                    {selectedUser.payment_history.map((txn, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-sm border ${
                          txn.status === 'paid' 
                            ? 'bg-status-success/10 border-status-success/30' 
                            : txn.status === 'refunded'
                            ? 'bg-red-900/20 border-red-500/30'
                            : 'bg-cockpit-bg border-cockpit-border'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-sm font-medium ${
                            txn.status === 'paid' 
                              ? 'text-status-success' 
                              : txn.status === 'refunded'
                              ? 'text-red-400'
                              : 'text-status-warning'
                          }`}>
                            {txn.status === 'paid' ? '✓ PAID' : txn.status === 'refunded' ? '↩ REFUNDED' : '⏳ PENDING'}
                          </span>
                          <span className="text-sm font-mono text-cockpit-text-primary">
                            £{txn.amount?.toFixed(2)} {txn.currency?.toUpperCase()}
                          </span>
                        </div>
                        <div className="text-xs text-cockpit-text-secondary space-y-1">
                          <p>Session: <span className="font-mono text-cockpit-text-primary/70">{txn.session_id?.slice(0, 24)}...</span></p>
                          <p>Date: {formatDate(txn.created_at)}</p>
                          {txn.refunded_at && (
                            <p className="text-red-400">Refunded: {formatDate(txn.refunded_at)}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-cockpit-text-secondary">No payment records found</p>
                )}
              </div>

              {/* Actions */}
              <div className="card-panel p-6">
                <h3 className="text-lg font-medium text-cockpit-text-primary mb-4">Account Actions</h3>
                
                {selectedUser.role === 'founder' ? (
                  /* Founder accounts - show protected label only */
                  <div className="flex items-center gap-2 p-3 bg-cockpit-bg border border-cockpit-border rounded-sm">
                    <Shield className="w-4 h-4 text-status-warning" />
                    <span className="text-sm text-cockpit-text-secondary">Founder Account (Protected)</span>
                  </div>
                ) : (
                  /* Regular users - show all actions */
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {/* Force Logout */}
                    <button
                      onClick={handleForceLogout}
                      disabled={actionLoading === 'logout' || !selectedUser.active_session}
                      className="btn-secondary flex items-center justify-center gap-2 py-3"
                      data-testid="force-logout-btn"
                    >
                      {actionLoading === 'logout' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <LogOut className="w-4 h-4" />
                      )}
                      Force Logout
                    </button>

                    {/* Send Password Reset */}
                    <button
                      onClick={handleSendReset}
                      disabled={actionLoading === 'reset'}
                      className="btn-secondary flex items-center justify-center gap-2 py-3"
                      data-testid="send-reset-btn"
                    >
                      {actionLoading === 'reset' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Mail className="w-4 h-4" />
                      )}
                      Send Password Reset
                    </button>

                    {/* Resend Verification - only show if not verified */}
                    {!selectedUser.is_verified && (
                      <button
                        onClick={handleResendVerification}
                        disabled={actionLoading === 'resend-verify'}
                        className="btn-secondary flex items-center justify-center gap-2 py-3 text-fsa-blue hover:bg-fsa-blue/10"
                        data-testid="resend-verification-btn"
                      >
                        {actionLoading === 'resend-verify' ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Mail className="w-4 h-4" />
                        )}
                        Resend Verification Email
                      </button>
                    )}

                    {/* Suspend/Reactivate */}
                    <button
                      onClick={() => setShowSuspendDialog(true)}
                      className={`flex items-center justify-center gap-2 py-3 ${
                        selectedUser.is_suspended 
                          ? 'btn-primary' 
                          : 'btn-secondary text-status-warning hover:bg-status-warning/10'
                      }`}
                      data-testid="suspend-btn"
                    >
                      <Ban className="w-4 h-4" />
                      {selectedUser.is_suspended ? 'Reactivate Account' : 'Suspend Account'}
                    </button>

                    {/* Delete Account */}
                    <button
                      onClick={() => setShowDeleteDialog(true)}
                      className="btn-destructive flex items-center justify-center gap-2 py-3"
                      data-testid="delete-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Account
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-cockpit-panel border-cockpit-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-cockpit-text-primary">Delete Account</AlertDialogTitle>
            <AlertDialogDescription className="text-cockpit-text-secondary">
              Are you sure you want to delete <strong>{selectedUser?.email}</strong>? 
              This action cannot be undone and will remove all user data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="btn-destructive"
              disabled={actionLoading === 'delete'}
            >
              {actionLoading === 'delete' ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Suspend Dialog */}
      <AlertDialog open={showSuspendDialog} onOpenChange={setShowSuspendDialog}>
        <AlertDialogContent className="bg-cockpit-panel border-cockpit-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-cockpit-text-primary">
              {selectedUser?.is_suspended ? 'Reactivate' : 'Suspend'} Account
            </AlertDialogTitle>
            <AlertDialogDescription className="text-cockpit-text-secondary">
              {selectedUser?.is_suspended 
                ? `Are you sure you want to reactivate ${selectedUser?.email}? They will be able to log in again.`
                : `Are you sure you want to suspend ${selectedUser?.email}? They will be logged out and unable to access the platform.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSuspend}
              className={selectedUser?.is_suspended ? 'btn-primary' : 'btn-destructive'}
              disabled={actionLoading === 'suspend'}
            >
              {actionLoading === 'suspend' ? 'Processing...' : (selectedUser?.is_suspended ? 'Reactivate' : 'Suspend')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Grant Edition Dialog */}
      <AlertDialog open={showGrantDialog} onOpenChange={setShowGrantDialog}>
        <AlertDialogContent className="bg-cockpit-panel border-cockpit-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-cockpit-text-primary">Grant Edition Access</AlertDialogTitle>
            <AlertDialogDescription className="text-cockpit-text-secondary">
              Select an edition to grant to <strong>{selectedUser?.email}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="my-4">
            <Select value={selectedEdition} onValueChange={setSelectedEdition}>
              <SelectTrigger className="input-field" data-testid="select-edition">
                <SelectValue placeholder="Select edition..." />
              </SelectTrigger>
              <SelectContent className="bg-cockpit-panel border-cockpit-border">
                {editions.map(edition => (
                  <SelectItem key={edition.id} value={edition.id}>{editionDisplayName(edition.name)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleGrantEdition}
              className="btn-primary"
              disabled={!selectedEdition || actionLoading === 'grant'}
            >
              {actionLoading === 'grant' ? 'Granting...' : 'Grant Access'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Database Migration Confirmation Dialog */}
      <AlertDialog open={showMigrationConfirm} onOpenChange={setShowMigrationConfirm}>
        <AlertDialogContent className="bg-cockpit-panel border-cockpit-border max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-cockpit-text-primary flex items-center gap-2">
              <Database className="w-5 h-5 text-fsa-blue" />
              Run Database Migrations
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-cockpit-text-secondary">
                <p className="mb-3">This will run all detected migration scripts:</p>
                
                {/* Auto-discovered scripts */}
                <div className="mb-4 p-3 bg-cockpit-bg border border-cockpit-border rounded-sm max-h-40 overflow-y-auto">
                  {migrationInfo?.available_scripts?.length > 0 ? (
                    <ul className="space-y-2 text-sm">
                      {migrationInfo.available_scripts.map((script, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <Database className="w-4 h-4 text-fsa-blue flex-shrink-0" />
                          <span>{script.display_name}</span>
                          <span className="text-xs text-cockpit-text-secondary/60">({script.filename})</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-cockpit-text-secondary">No migration scripts detected</p>
                  )}
                </div>

                {/* Protected data notice */}
                <div className="p-3 bg-status-success/10 border border-status-success/30 rounded-sm mb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Shield className="w-4 h-4 text-status-success" />
                    <span className="text-xs font-medium text-status-success uppercase tracking-wider">Protected</span>
                  </div>
                  <p className="text-xs text-status-success/80">
                    User accounts, sessions, payments, and purchases are NEVER modified.
                  </p>
                </div>

                <p className="text-status-warning text-sm">
                  Are you sure you want to sync content to production?
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRunMigrations}
              className="btn-primary"
              disabled={migrationLoading}
            >
              {migrationLoading ? 'Running...' : 'Sync Content'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* BULK DELETE — Double Confirmation Dialog */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={(open) => {
        setShowBulkDeleteDialog(open);
        if (!open) setBulkDeleteConfirmText('');
      }}>
        <AlertDialogContent className="bg-cockpit-panel border-cockpit-border max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Bulk Delete — {selectedUserIds.size} User{selectedUserIds.size !== 1 ? 's' : ''}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-cockpit-text-secondary space-y-3">
                <div className="p-3 bg-red-900/20 border border-red-700/50 rounded-sm">
                  <p className="text-sm text-red-300 font-medium">This action is PERMANENT and IRREVERSIBLE.</p>
                  <p className="text-xs text-red-300/70 mt-1">
                    All selected user accounts and their associated data will be permanently deleted.
                  </p>
                </div>
                
                <div className="p-3 bg-status-success/10 border border-status-success/30 rounded-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <Shield className="w-4 h-4 text-status-success" />
                    <span className="text-xs font-medium text-status-success uppercase tracking-wider">Safety Check</span>
                  </div>
                  <p className="text-xs text-status-success/80">
                    The server will individually verify each user has NO active editions, NO products, NO payment history, and is NOT a founder before proceeding. If any user fails verification, the entire operation is aborted.
                  </p>
                </div>

                <div>
                  <label className="text-xs text-cockpit-text-secondary block mb-2">
                    Type <span className="font-mono font-bold text-red-400">DELETE UNPAID USERS</span> to confirm:
                  </label>
                  <input
                    type="text"
                    value={bulkDeleteConfirmText}
                    onChange={(e) => setBulkDeleteConfirmText(e.target.value)}
                    placeholder="Type confirmation text..."
                    className="input-field w-full font-mono text-sm"
                    data-testid="bulk-delete-confirm-input"
                    autoComplete="off"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
            <button
              onClick={handleBulkDelete}
              disabled={bulkDeleteConfirmText !== 'DELETE UNPAID USERS' || bulkDeleteLoading}
              className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                bulkDeleteConfirmText === 'DELETE UNPAID USERS'
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-cockpit-border text-cockpit-text-secondary cursor-not-allowed'
              }`}
              data-testid="bulk-delete-confirm-btn"
            >
              {bulkDeleteLoading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Deleting...
                </span>
              ) : (
                `Permanently Delete ${selectedUserIds.size} Users`
              )}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPage;
